import {
  Badge,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedState, useIsFirstRender } from "@mantine/hooks";
import { IconCalendar, IconSearch } from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_Row,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, {
  memo,
  useCallback,
  useEffect,
  useState,
  useTransition,
} from "react";
import {
  repositoryDelivery,
  styleTableMantine,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import useDynamicHeight from "../../../_base/component/Core/hookBase";
import { paginationBase } from "../../../_base/model/_base/BaseTable";
import {
  formatDateNotTimeZone,
  formatDateTransfer,
} from "../../../common/FormatDate/FormatDate";
import { _localization } from "../../../config/location";
import { green } from "../../../const/variables";
import DeliveryList from "./DeliveryList";
import { useLocation } from "react-router-dom";

type DeliveryAssignmentDetail = {
  ngayCt: string;
  maCt: string;
  ngayPc: string;
  soCtGoc: string;
  maKh: string;
  tenKh: string;
  telephoneNumber: string;
  deptId: number;
  tenBp: string;
  soHd: string;
  ngayHd: string;
  dcNhan: string;
  branchId: number;
  maCn: string;
  tenCn: string;
  sourceType: number;
  dcGiao: string;
  ghiChuGh: string | null;
  ngayGh: string | null;
  ttGh: string;
  tTienDh: number;
  ttDh: string;
  trangThaiTt: string;
  ttTienMat: number | null;
  ttChuyenKhoan: number | null;
  tongTienTt: number;
  tgXuatKho: string;
  maNguoiGh: string;
  tenNguoiGh: string;
  tinhLuong: boolean;
  nguoiLaiXe: string;
  receptionTime: string | null;
  deliveryTime: string | null;
  arrivalTime: string | null;
  completionTime: string | null;
  writeNumberOrder: string | null;
  tetCompletionTime: string | null;
  distance: number;
  distanceTotal: number;
  distanceEstimated: number;
  durationEstimated: number;
  tongPhaiThanhToan: number;
  tongTienDonHang: number;
  tongTienDaThanhToan: number;
  tienGiuiXe: number | null;
  ghiChuDh: string | null;
  hinhThucTt: string;
  tenKd: string;
  codeKd: string;
  assignmentDetailId: number;
  modifiedDistance: number;
  modifiedPayroll: boolean;
  modifiedStatusId: number;
  modifiedStatusName: string;
  noteByEmployee: string | null;
  totalDeliveryTime: string | null;
  totalEstimatedDuration: string;
  timeDifference: string | null;
  departmentId: number;
  departmentName: string;
  provinceId: number;
  provinceName: string;
  districtId: number;
  districtName: string;
  communeId: number;
  communeName: string;
  payrollModifiedDate: string | null;
  deliveryId: number;
};

type Delivery = {
  deliveryId: number;
  rowNum: number;
  customerId: number;
  customerCode: string;
  customerName: string;
  companyName: string | null;
  orderDate: string;
  sourceCode: string;
  toAddress: string;
  assignmentCount: number;
  masterOrg: string;
  deliveryAssignmentDetails: DeliveryAssignmentDetail[];
};

const DeliveryInOneDay = () => {
  const height = useDynamicHeight(280);
  const [isPending, startTransition] = useTransition();

  // Function to get the first and last day of the current month
  const getMonthDateRange = () => {
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return {
      firstDay: formatDateNotTimeZone(firstDay),
      lastDay: formatDateNotTimeZone(lastDay),
    };
  };

  //data and fetching state
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<Delivery[]>([]);
  // const [dataTemp, setDataTemp] = useState<TblDeliveryAssignment[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";

  //table state

  const [pagination, setPagination] = useState(paginationBase);
  const dateRange = getMonthDateRange();
  const [search, setSearch] = useState({
    code: _searchJson && checkedPinSearch ? _searchJson?.code : "",
    FromDate:
      _searchJson && checkedPinSearch
        ? _searchJson?.FromDate
        : dateRange.firstDay,
    ToDate:
      _searchJson && checkedPinSearch ? _searchJson?.ToDate : dateRange.lastDay,
  });
  const firstRender = useIsFirstRender();
  const [keySearch, setKeySearch] = useDebouncedState(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300
  );

  const columns = React.useMemo<MRT_ColumnDef<Delivery>[]>(
    () => [
      {
        accessorKey: "sourceCode",
        header: "Số đơn hàng",
        Cell: ({ renderedCellValue }) => (
          <Badge
            radius="sm"
            variant="dot"
            size="lg"
            style={{ cursor: "pointer" }}
            color={renderedCellValue == null ? "red" : green.base}
          >
            {renderedCellValue ?? "N/A"}
          </Badge>
        ),
      },
      {
        accessorKey: "customerCode",
        header: "Mã khách hàng",
      },
      {
        accessorKey: "customerName",
        header: "Tên khách hàng",
      },
      {
        accessorKey: "masterOrg",
        header: "Chi nhánh",
      },
      {
        accessorKey: "companyName",
        header: "Tên doanh nghiệp",
        size: 225,
      },
      {
        accessorKey: "toAddress",
        header: "Địa điểm giao hàng",

        size: 300,
      },
      {
        accessorKey: "orderDate",
        header: "Ngày lên đơn giao hàng",
        size: 240,
        Cell: ({ renderedCellValue }: any) => (
          <Text size="sm">
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "assignmentCount",
        header: "Tổng số vận đơn",
      },
      {
        accessorKey: "assignmentCodes",
        header: "Tổng hợp mã vận đơn",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 200,
      },
    ],
    []
  );
  const handleChangeSearchValue = (value: string, key: string) => {
    if (value !== "Invalid date")
      setSearch((prevData) => ({ ...prevData, [key]: value }));
    else {
      setSearch((prevData) => ({ ...prevData, [key]: "" }));
    }
  };
  const fetchData = async (typeGet?: string) => {
    setIsLoading(true);
    let url = `?Skip=${pagination?.pageIndex * pagination?.pageSize}&Take=${
      pagination.pageSize
    }`;

    // Always include date range on initial load and if values are provided
    if (search.FromDate) {
      url += `&FromDate=${search.FromDate}`;
    }
    if (search.ToDate) {
      url += `&ToDate=${search.ToDate}`;
    }

    if (keySearch.length > 0) {
      url += `&KeySearch=${keySearch}`;
    }

    try {
      const response = await repositoryDelivery.get(
        `api/v1/TblDelivery/get-list-delivery-assignment-by-delivery-in-one-day${url}`
      );

      if (response && response.success) {
        let result = response.data;
        setData(result);
        startTransition(() => {
          setRowCount(response.totalCount);
          table.resetRowSelection();
        });
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({ ...search, keySearch: keySearch })
      );
    }
  };
  useEffect(() => {
    // Do not fetch on first render (handled by the other useEffect)
    if (!firstRender) fetchData();
  }, [pagination.pageSize, pagination.pageIndex]);
  const MemoizedDeliveryList = React.memo(DeliveryList);

  const DeliveryWrapper = memo(
    ({ deliveryAssignmentDetails }: { deliveryAssignmentDetails: any }) => {
      return (
        <MemoizedDeliveryList
          dataDetail={deliveryAssignmentDetails}
          isSetHeight={false}
        />
      );
    }
  );

  const renderDetailPanel = useCallback(
    ({ row }: { row: MRT_Row<Delivery> }) => {
      if (!row.original) return <></>;
      return (
        <DeliveryWrapper
          key={row.id}
          deliveryAssignmentDetails={row.original.deliveryAssignmentDetails}
        />
      );
    },
    []
  );
  useEffect(() => {
    // Call API on component mount with default date values
    fetchData("initial");
  }, []);
  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: true,
    enableTopToolbar: false,
    positionToolbarAlertBanner: "bottom",
    renderTopToolbarCustomActions: ({ table }) => <></>,
    renderToolbarInternalActions: ({ table }) => <></>,
    renderDetailPanel: renderDetailPanel,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    getRowId: (row) => row.deliveryId?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-select", "STT", "sourceCode"],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    // onRowSelectionChange: setRowSelection,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    state: {
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isLoading,
      //  showSkeletons: isLoading,
      //rowSelection,
    },
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["25", "50"],
    },
    // mantineTableBodyRowProps: ({ row }) => ({
    //   onClick: row.getToggleSelectedHandler(),
    //   sx: { cursor: "pointer" },
    // }),
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
  });
  const styleGrid = { base: 6, xs: 4, sm: 3, md: 3, lg: 2 };
  return (
    <>
      <div ref={headerRef} style={{ width: "100%" }}>
        <Flex justify={"space-between"} wrap={"wrap"}>
          <_breadcrumb></_breadcrumb>
        </Flex>
        <Flex gap={"md"} align={"center"} wrap={"wrap"} mb={10} mt={10}>
          <Grid gutter={"xs"} w={"100%"}>
            <Grid.Col span={styleGrid}>
              <TextInput
                placeholder="Nhập từ khóa"
                leftSection={<IconSearch color="#15aabf" />}
                defaultValue={keySearch ?? ""}
                onChange={(e) => {
                  setKeySearch(e.currentTarget.value);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    await fetchData();
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={styleGrid}>
              {" "}
              <DatePickerInput
                size="sm"
                placeholder="Từ ngày"
                locale="vi"
                valueFormat="DD/MM/YYYY"
                value={
                  search?.FromDate && search?.FromDate?.length > 0
                    ? new Date(search?.FromDate)
                    : null
                }
                onChange={(e) =>
                  handleChangeSearchValue(
                    formatDateNotTimeZone(e) ?? "",
                    "FromDate"
                  )
                }
                leftSection={<IconCalendar color="#15aabf" />}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={styleGrid}>
              {" "}
              <DatePickerInput
                size="sm"
                placeholder="Đến ngày"
                locale="vi"
                valueFormat="DD/MM/YYYY"
                value={
                  search?.ToDate && search?.ToDate?.length > 0
                    ? new Date(search?.ToDate)
                    : null
                }
                onChange={(e) =>
                  handleChangeSearchValue(
                    formatDateNotTimeZone(e) ?? "",
                    "ToDate"
                  )
                }
                leftSection={<IconCalendar color="#15aabf" />}
                clearable
              />
            </Grid.Col>

            <Grid.Col span={{ base: 6, md: 6, lg: 1.25 }}>
              <Button
                leftSection={<IconSearch size={14} />}
                color="blue"
                variant="outline"
                onClick={async () => {
                  await fetchData();
                }}
              >
                Tìm kiếm
              </Button>
            </Grid.Col>
          </Grid>
        </Flex>
      </div>
      <div className="mt-5">
        <MantineReactTable table={table} />
      </div>
    </>
  );
};

export default DeliveryInOneDay;
